import type { ModelDirectoryObserver } from "@/utils/loadModelFile"
import type { ModelFacade } from "@/utils/useEngineModel/ModelFacade"
import { ConfigureModelIntent, ConfigureModelLayers, Layer, LibraryIdentifier } from "@kaleidoscope-solutions/engine-wasm"
import { defineStore } from "pinia"
import { promptDownload } from "@/utils/"
import { loadChunksFromZipFile as extractChunksFromZipFile, loadAppZip } from "@/utils/loadModelFile"
import { captureError } from "@/utils/useEngineModel"

export enum UserSetupOption {
  Nothing = "Nothing",
  Discard = "Discard",
  Upload = "Upload"
}

export enum OrgSetupOption {
  Nothing = "Nothing",
  ResetEmpty = "ResetEmpty",
  ResetDemo = "ResetDemo",
  Upload = "Upload"
}

export enum BaseSetupOption {
  Nothing = "Nothing",
  Library = "Library",
  Upload = "Upload"
}

export interface ApplicationSetup {
  user: UserSetupOption
  userFiles?: File[]

  org: OrgSetupOption
  orgFiles?: File[]

  base: BaseSetupOption
  baseFiles?: File[]
}

interface State {
  model?: ModelFacade
  setupDialog: boolean
  exportDialog: boolean
  modelDirectoryObserver?: ModelDirectoryObserver
}

export const useSetupStore = defineStore("setup", {
  state: (): State => ({
    model: undefined,
    setupDialog: false,
    exportDialog: false,
  }),
  getters: {
    modelApi(state) {
      if (!state.model) {
        throw new Error("Model not initialised")
      }
      return state.model.api
    },
  },
  actions: {
    initialise(model: ModelFacade, observer?: ModelDirectoryObserver) {
      this.model = model
      this.modelDirectoryObserver = observer
    },
    applyChunksAsOrg(_chunks: string[], _fileNames: string[]) {
      throw new Error("applyChunkAsOrg should never be called")
    },
    async applySettings(settings: ApplicationSetup) {
      try {
        const layers = await this.buildConfigLayers(settings)
        await this.configureModel(layers)

        return { success: true }
      } catch (error: any) {
        console.warn("Save settings error:", error?.message)
        throw error
      }
    },
    async buildConfigLayers(settings: ApplicationSetup) {
      const baseIntent = await this.buildBaseIntent(settings)
      const orgIntent = await this.buildOrgIntent(settings)
      const userIntent = await this.buildUserIntent(settings)

      return new ConfigureModelLayers(baseIntent, orgIntent, userIntent)
    },
    async buildBaseIntent(settings: ApplicationSetup) {
      switch (settings.base) {
        case BaseSetupOption.Nothing:
          return ConfigureModelIntent.noChange()
        case BaseSetupOption.Library:
          // TODO(emily): This should go away when we have the library layer in model-persistence and the engine.
          switch (settings.org) {
            case OrgSetupOption.ResetDemo: {
              return ConfigureModelIntent.library(LibraryIdentifier.Demo)
            }
            case OrgSetupOption.ResetEmpty: {
              return ConfigureModelIntent.library(LibraryIdentifier.Default)
            }
          }
          return ConfigureModelIntent.library(LibraryIdentifier.Default)
        case BaseSetupOption.Upload:
          return await this.parseUploadedFiles(settings.baseFiles)
      }
    },
    async buildOrgIntent(settings: ApplicationSetup) {
      switch (settings.org) {
        case OrgSetupOption.Nothing:
          return ConfigureModelIntent.noChange()
        case OrgSetupOption.ResetEmpty:
          // TODO(emily): Temporary while we get the library layer into model-persistence and the engine
          // const chunks = await this.loadModelFromZipPath(Solutions.Default)
          // return ConfigureModelIntent.()
          return ConfigureModelIntent.library(LibraryIdentifier.Default)
        case OrgSetupOption.ResetDemo:
          return ConfigureModelIntent.library(LibraryIdentifier.Demo)
        case OrgSetupOption.Upload:
          return await this.parseUploadedFiles(settings.orgFiles)
      }
    },
    async buildUserIntent(settings: ApplicationSetup) {
      switch (settings.user) {
        case UserSetupOption.Nothing:
          return ConfigureModelIntent.noChange()
        case UserSetupOption.Discard:
          return ConfigureModelIntent.remove()
        case UserSetupOption.Upload:
          return await this.parseUploadedFiles(settings.userFiles)
      }
    },
    async export(selectedLayers: Layer[]) {
      if (selectedLayers.length === 0) {
        throw new Error("Must select at least one option to export")
      }

      try {
        const { chunks, fileNames } = await this.exportLayers(selectedLayers)
        if (chunks.length === 0) {
          throw new Error("No data available to export")
        }
        await promptDownload("export.kaleidoscope.definition.zip", chunks, fileNames)
        return { success: true }
      } catch (error: any) {
        console.warn("Export error:", error?.message)
        throw error
      }
    },
    async validateFile(file: File) {
      const name = file.name.toLowerCase()
      const allowedExt = [".zip", ".yaml", ".yml"]
      if (!allowedExt.some((ext) => name.endsWith(ext))) {
        throw new Error("Unsupported file type (only .zip, .yaml, .yml supported for configuration)")
      }

      try {
        if (name.endsWith(".zip")) {
          await extractChunksFromZipFile(file)
        } else {
          await file.text()
        }
      } catch (err: any) {
        throw new Error(`Failed to read file: ${err?.message ?? String(err)}`)
      }
    },
    async parseUploadedFiles(files?: File[]) {
      // TODO(emily): We probably can't get here, but what should we do if a user is trying to upload a file
      // manages to press the button but hasnt actually uplaoded a file?
      if (!files || files.length === 0) {
        return ConfigureModelIntent.noChange()
      }

      if (files.length === 1) {
        const file = files[0]
        const name = file.name.toLowerCase()

        if (name.endsWith(".yaml") || name.endsWith(".yml")) {
          const content = await file.text()
          return ConfigureModelIntent.replaceBlob(content)
        }

        if (name.endsWith(".zip")) {
          const { fileContents, fileNames } = await extractChunksFromZipFile(file)
          return ConfigureModelIntent.replaceChunks(fileContents, fileNames)
        }

        throw new Error("Unsupported uploaded file type")
      }

      // We only support uploading multiple yamls, not multiple zips
      const names = []

      for (const file of files) {
        const name = file.name
        if (!name.endsWith(".yaml") && !name.endsWith(".yml")) {
          throw new Error("Unsupported uploaded file type")
        }
        names.push(name)
      }

      const contents = await Promise.all(files.map(async (file) => await file.text()))
      return ConfigureModelIntent.replaceChunks(contents, names)
    },
    async exportLayers(layers: Layer[]) {
      const chunks: string[] = []
      const fileNames: string[] = []

      const layerDir = (layer: Layer) => {
        switch (layer) {
          case Layer.User:
            return "user"
          case Layer.Org:
            return "org"
          case Layer.Base:
          default:
            return "base"
        }
      }

      for (const layer of layers) {
        const result = await this.exportLayer(layer)
        const dir = layerDir(layer)

        for (let i = 0; i < result.chunks.length; i++) {
          const chunk = result.chunks[i]
          const name = result.filenames[i] ?? `chunk-${i + 1}.yaml`
          chunks.push(chunk)
          fileNames.push(`${dir}/${name}`)
        }
      }

      return { chunks, fileNames }
    },
    async exportLayer(selectedLayer: Layer) {
      if (!this.model) {
        throw new Error("Model not initialised")
      }

      try {
        const saveResult: any = await this.modelApi.saveModelLayerToChunks(selectedLayer)
        const chunks = saveResult.chunks
        const filenames = this.normalizeFilenames(saveResult, chunks.length)

        return { chunks, filenames }
      } catch (err: any) {
        throw new Error(`Failed to export layer '${String(selectedLayer)}': ${err?.message ?? String(err)}`)
      }
    },
    normalizeFilenames(saveResult: any, chunksLength: number): string[] {
      let filenames: string[] = []

      if (Array.isArray(saveResult.fileNames)) {
        filenames = saveResult.fileNames
      } else if (Array.isArray(saveResult.file_names)) {
        filenames = saveResult.file_names
      } else if (typeof saveResult.file_names === "function") {
        filenames = Array.from(saveResult.file_names())
      }

      if (!filenames.length) {
        filenames = Array.from({ length: chunksLength }, (_, i) => `chunk-${i + 1}.yaml`)
      } else if (filenames.length !== chunksLength) {
        filenames = filenames.slice(0, chunksLength)
        while (filenames.length < chunksLength) {
          filenames.push(`chunk-${filenames.length + 1}.yaml`)
        }
      }

      return filenames
    },
    async stopObserver() {
      try {
        await this.modelDirectoryObserver?.stopObserver?.()
      } catch (e) {
        console.warn("Failed to stop model directory observer:", e)
      }
    },
    async configureModel(layers: ConfigureModelLayers) {
      if (!this.model) {
        throw new Error("Model not initialised")
      }

      await this.stopObserver()

      this.model.configureModelAndReload(layers)
    },
    async chunksFromAppZip(zip: string | { path: string; text?: string }) {
      const path = typeof zip === "string" ? zip : zip.path
      const text = typeof zip === "string" ? undefined : zip.text

      await this.stopObserver()

      try {
        return await loadAppZip(path)
      } catch (error) {
        captureError(error, { snackbar: `Unable to load model '${text ?? path}' from path: '${path}'.` })
        throw error
      }
    },
  },
})
